import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { useTheme } from '../../../contexts/ThemeContext';

const SkillsEvolutionSection = () => {
  const { isDark } = useTheme();
  const [animatedSkills, setAnimatedSkills] = useState({});

  const skillsData = [
    {
      category: "Frontend Development",
      icon: "Monitor",
      color: "primary",
      skills: [
        { name: "React.js", level: 95, years: "4 years", trend: "up" },
        { name: "TypeScript", level: 90, years: "3 years", trend: "up" },
        { name: "Next.js", level: 85, years: "2 years", trend: "up" },
        { name: "Tailwind CSS", level: 92, years: "3 years", trend: "stable" },
        { name: "JavaScript", level: 94, years: "4 years", trend: "stable" }
      ]
    },
    {
      category: "Backend Development",
      icon: "Server",
      color: "success",
      skills: [
        { name: "Node.js", level: 88, years: "4 years", trend: "up" },
        { name: "Express.js", level: 90, years: "4 years", trend: "stable" },
        { name: "Python", level: 82, years: "3 years", trend: "up" },
        { name: "PostgreSQL", level: 85, years: "3 years", trend: "up" },
        { name: "MongoDB", level: 87, years: "4 years", trend: "stable" }
      ]
    },
    {
      category: "DevOps & Tools",
      icon: "Settings",
      color: "brand-purple",
      skills: [
        { name: "Docker", level: 80, years: "2 years", trend: "up" },
        { name: "AWS", level: 75, years: "2 years", trend: "up" },
        { name: "Git", level: 93, years: "4 years", trend: "stable" },
        { name: "CI/CD", level: 78, years: "2 years", trend: "up" },
        { name: "Linux", level: 83, years: "3 years", trend: "stable" }
      ]
    },
    {
      category: "Soft Skills",
      icon: "Users",
      color: "accent",
      skills: [
        { name: "Team Leadership", level: 85, years: "2 years", trend: "up" },
        { name: "Problem Solving", level: 92, years: "4 years", trend: "up" },
        { name: "Communication", level: 88, years: "4 years", trend: "up" },
        { name: "Mentoring", level: 80, years: "1 year", trend: "up" },
        { name: "Project Management", level: 82, years: "2 years", trend: "up" }
      ]
    }
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      const animated = {};
      skillsData.forEach(category => {
        category.skills.forEach(skill => {
          animated[skill.name] = skill.level;
        });
      });
      setAnimatedSkills(animated);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const getColorClasses = (color) => {
    const colorMap = {
      primary: {
        bg: "bg-primary",
        text: "text-primary",
        light: "bg-primary/10"
      },
      success: {
        bg: "bg-success",
        text: "text-success",
        light: "bg-success/10"
      },
      "brand-purple": {
        bg: "bg-brand-purple",
        text: "text-brand-purple",
        light: "bg-brand-purple/10"
      },
      accent: {
        bg: "bg-accent",
        text: "text-accent",
        light: "bg-accent/10"
      }
    };
    return colorMap[color] || colorMap.primary;
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case "up":
        return <Icon name="TrendingUp" size={14} className="text-success" />;
      case "down":
        return <Icon name="TrendingDown" size={14} className="text-error" />;
      default:
        return <Icon name="Minus" size={14} className="text-text-muted" />;
    }
  };

  return (
    <section className={`py-20 ${isDark ? 'bg-gradient-to-br from-slate-900 to-slate-800' : 'bg-gradient-to-br from-slate-50 to-blue-50'}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="BarChart3" size={16} />
            <span>Skills Evolution</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            Technical Expertise Growth
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Watch how my technical skills have evolved over 4+ years of continuous learning, 
            hands-on experience, and staying current with industry trends.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {skillsData.map((category, categoryIndex) => {
            const colors = getColorClasses(category.color);
            
            return (
              <div key={categoryIndex} className={`${isDark ? 'bg-slate-800 border-slate-600' : 'bg-white border-border'} rounded-2xl border shadow-premium p-8`}>
                <div className="flex items-center space-x-4 mb-8">
                  <div className={`w-12 h-12 ${colors.bg} rounded-xl flex items-center justify-center`}>
                    <Icon name={category.icon} size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-text-primary">{category.category}</h3>
                    <p className="text-text-muted text-sm">Professional proficiency levels</p>
                  </div>
                </div>

                <div className="space-y-6">
                  {category.skills.map((skill, skillIndex) => (
                    <div key={skillIndex} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-text-primary">{skill.name}</span>
                          {getTrendIcon(skill.trend)}
                        </div>
                        <div className="flex items-center space-x-2 text-sm text-text-muted">
                          <span>{skill.years}</span>
                          <span className="font-semibold">{skill.level}%</span>
                        </div>
                      </div>
                      
                      <div className="relative">
                        <div className="w-full bg-surface rounded-full h-2">
                          <div 
                            className={`${colors.bg} h-2 rounded-full transition-all duration-1000 ease-out`}
                            style={{ 
                              width: `${animatedSkills[skill.name] || 0}%` 
                            }}
                          ></div>
                        </div>
                        
                        {/* Skill Level Indicator */}
                        <div 
                          className="absolute top-0 h-2 w-1 bg-white rounded-full shadow-sm transition-all duration-1000 ease-out"
                          style={{ 
                            left: `${(animatedSkills[skill.name] || 0)}%`,
                            transform: 'translateX(-50%)'
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Category Summary */}
                <div className={`mt-8 p-4 ${colors.light} rounded-xl border border-${category.color}/20`}>
                  <div className="flex items-center space-x-2">
                    <Icon name="Award" size={16} className={colors.text} />
                    <span className={`text-sm font-medium ${colors.text}`}>
                      {category.category === "Frontend Development" && "Expert in modern React ecosystem and responsive design"}
                      {category.category === "Backend Development" && "Proficient in scalable server-side architecture"}
                      {category.category === "DevOps & Tools" && "Experienced in deployment and infrastructure management"}
                      {category.category === "Soft Skills" && "Strong leadership and communication abilities"}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Learning Philosophy */}
        <div className="mt-16 bg-white rounded-2xl border border-border shadow-premium p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-primary to-brand-purple rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Icon name="BookOpen" size={32} className="text-white" />
            </div>
            <h3 className="text-2xl font-bold text-text-primary mb-2">Continuous Learning Philosophy</h3>
            <p className="text-text-secondary">My approach to staying current in the ever-evolving tech landscape</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto">
                <Icon name="Target" size={24} className="text-primary" />
              </div>
              <h4 className="font-semibold text-text-primary">Goal-Oriented Learning</h4>
              <p className="text-text-secondary text-sm">
                I set specific learning goals each quarter and track progress through practical projects and certifications.
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-success/10 rounded-xl flex items-center justify-center mx-auto">
                <Icon name="Users" size={24} className="text-success" />
              </div>
              <h4 className="font-semibold text-text-primary">Community Engagement</h4>
              <p className="text-text-secondary text-sm">
                Active participation in tech communities, conferences, and open-source projects keeps me connected to industry trends.
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-brand-purple/10 rounded-xl flex items-center justify-center mx-auto">
                <Icon name="Zap" size={24} className="text-brand-purple" />
              </div>
              <h4 className="font-semibold text-text-primary">Hands-On Practice</h4>
              <p className="text-text-secondary text-sm">
                Every new technology I learn is immediately applied in personal projects or integrated into existing work.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SkillsEvolutionSection;